import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Contexto
import { AuthProvider } from './context/AuthContext';

// Componentes
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRoute from './components/RoleBasedRoute';

// Páginas
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import { ClientList, ClientForm, ClientDetail } from './pages/clients';
import { BranchList, BranchForm, BranchDetail } from './pages/branches';
import { UserList, UserForm, UserDetail } from './pages/users';
import { TransferList, TransferForm, TransferDetail } from './pages/transfers';
import { ShipmentList, ShipmentForm, ShipmentDetail } from './pages/shipments';
import { BalanceMovementList } from './pages/balances';

// Tema personalizado
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#f50057',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Rutas públicas */}
            <Route path="/login" element={<Login />} />
            
            {/* Rutas protegidas */}
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                
                {/* Rutas de Clientes - Accesibles para Agentes, Supervisores y Administradores */}
                <Route element={<RoleBasedRoute allowedRoles={['Agente', 'Supervisor', 'Administrador']} />}>
                  <Route path="/clients" element={<ClientList />} />
                  <Route path="/clients/view/:id" element={<ClientDetail />} />
                </Route>
                
                {/* Rutas de Clientes - Solo para Supervisores y Administradores */}
                <Route element={<RoleBasedRoute allowedRoles={['Supervisor', 'Administrador']} />}>
                  <Route path="/clients/create" element={<ClientForm />} />
                  <Route path="/clients/edit/:id" element={<ClientForm />} />
                </Route>
                
                {/* Rutas de Sucursales - Solo para Supervisores y Administradores */}
                <Route element={<RoleBasedRoute allowedRoles={['Supervisor', 'Administrador']} />}>
                  <Route path="/branches" element={<BranchList />} />
                  <Route path="/branches/create" element={<BranchForm />} />
                  <Route path="/branches/edit/:id" element={<BranchForm />} />
                  <Route path="/branches/view/:id" element={<BranchDetail />} />
                </Route>
                
                {/* Rutas de Usuarios - Solo para Supervisores y Administradores */}
                <Route element={<RoleBasedRoute allowedRoles={['Supervisor', 'Administrador']} />}>
                  <Route path="/users" element={<UserList />} />
                  <Route path="/users/view/:id" element={<UserDetail />} />
                </Route>
                
                {/* Rutas de Usuarios - Solo para Administradores */}
                <Route element={<RoleBasedRoute allowedRoles={['Administrador']} />}>
                  <Route path="/users/create" element={<UserForm />} />
                  <Route path="/users/edit/:id" element={<UserForm />} />
                </Route>
                
                {/* Rutas de Transferencias - Accesibles para Agentes, Supervisores y Administradores */}
                <Route element={<RoleBasedRoute allowedRoles={['Agente', 'Supervisor', 'Administrador']} />}>
                  <Route path="/transfers" element={<TransferList />} />
                  <Route path="/transfers/create" element={<TransferForm />} />
                  <Route path="/transfers/view/:id" element={<TransferDetail />} />
                </Route>
                
                {/* Rutas de Envíos de Dinero - Accesibles para Portavalores, Supervisores y Administradores */}
                <Route element={<RoleBasedRoute allowedRoles={['Portavalor', 'Supervisor', 'Administrador']} />}>
                  <Route path="/shipments" element={<ShipmentList />} />
                  <Route path="/shipments/create" element={<ShipmentForm />} />
                  <Route path="/shipments/view/:id" element={<ShipmentDetail />} />
                </Route>
                
                {/* Rutas de Movimientos de Balance - Solo para Supervisores y Administradores */}
                <Route element={<RoleBasedRoute allowedRoles={['Supervisor', 'Administrador']} />}>
                  <Route path="/balances" element={<BalanceMovementList />} />
                </Route>
                
                <Route path="/settings" element={<div>Configuración</div>} />
                <Route path="/profile" element={<div>Mi Perfil</div>} />
              </Route>
            </Route>
            
            {/* Redirección por defecto */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
